import React, { useState, useEffect } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { useTranslation } from 'react-i18next';
import AdSense from 'react-adsense';
import packageJson from '../../../package.json';
import AwsChatbot from '../AwsChatbot';

const RetryImage = ({ src, alt, className, style, maxRetries = 3 }) => {
  const [retryCount, setRetryCount] = useState(0);
  const [failed, setFailed] = useState(false);

  const handleError = () => {
    if (retryCount < maxRetries) {
      setTimeout(() => {
        setRetryCount(prev => prev + 1);
      }, 1500 * (retryCount + 1));
    } else {
      setFailed(true);
    }
  };

  if (failed) return null;

  return (
    <img 
      key={`${src}-${retryCount}`}
      src={retryCount > 0 ? `${src}?retry=${retryCount}` : src} 
      alt={alt} className={className} style={style} onError={handleError} 
    />
  );
};

const ProfilePage = ({ healthCards = [] }) => {
  const { oktaAuth, authState } = useOktaAuth();
  const { t, i18n } = useTranslation();
  const [user, setUser] = useState(null);
  const [langValue, setLangValue] = useState(i18n.language || "en");
  const isPro = user?.tier === 'PRO';

  // Sanitize email for S3 path compatibility: email@domain.com -> email-at-domain_com
  const sanitizedEmail = user.email ? user.email.replace(/@/g, '-at-').replace(/\./g, '_') : 'guest';

  useEffect(() => {
    if (authState?.isAuthenticated) {
      oktaAuth.getUser().then(setUser);
    }
  }, [authState, oktaAuth]);

  const onLanguageHandle = (event) => {
    let newLang = event.target.value;
    setLangValue(newLang);
    i18n.changeLanguage(newLang);
  };

  const renderRadioButtons = () => {
    return (
      <div>
        <div className="Header_locate_en_img">
          <input
            checked={langValue === 'en'}
            name="language" onChange={onLanguageHandle} value="en" type="radio"/>
        </div> 
        <div className="Header_locate_ru_img">
          <input
            checked={langValue === 'ru'}
            name="language" onChange={onLanguageHandle} value="ru" type="radio"/>
        </div>
        <div className="Header_locate_es_img">
          <input
            checked={langValue === 'es'}
            name="language" onChange={onLanguageHandle} value="es" type="radio"/>
        </div>
        <div className="Header_locate_hd_img">
          <input
            checked={langValue === 'hd'}
            name="language" onChange={onLanguageHandle} value="hd" type="radio"/>
        </div>
        <div className="Header_locate_fr_img">
          <input
            checked={langValue === 'fr'}
            name="language" onChange={onLanguageHandle} value="fr" type="radio"/>
        </div>
      </div>
    );
  };

  if (!user) return null;

  return (
    <div className="profile-wrapper px-3">
      <section className="pro-appreciation">
        {isPro && (
          <div className="pro-banner alert alert-warning shadow-sm">
            <h2>Thank you for being a Pro User!</h2>
            <p>Your 24,99€ monthly contribution directly supports the HealthDash open-source ecosystem.</p>
          </div>
        )}
      </section>

      <section className="localization-settings mb-4">
        <h4>{t('header.menu1.title')}</h4>
        {renderRadioButtons()}
      </section>

      <div className="ecosystem-summary mb-4">
        <h4>Ecosystem Status (v{packageJson.version})</h4>
        <p>Current Tier: <strong>{user.tier || 'STANDARD'}</strong></p>
        <ul>
          <li>Sync Status: <span className="text-success">Active</span></li>
          <li>Last Feature Used: {user.lastActivity || 'Dashboard Overview'}</li>
          <li>PCD Status: {packageJson.config.enablePcdExtensions === 'true' ? 'Enabled (Alpha)' : 'Standard'}</li>
        </ul>
      </div>

      <div className="cloud-links mb-4">
        <h4>{t('header.menu2.title')}</h4>
        <label><strong>{t('header.menu3.title')}:</strong> {user.name || user.email}</label>
        <p className="mt-2 mb-1">{t('header.menu4.title')}</p>
        <ul className="list-unstyled">
          <li>{t('header.menu5.title')}: <a href={`https://www.myclouddashboard.healthdash.lv?user=${user.name || user.email}`} target="_blank" rel="noreferrer">{user.name || 'User Link'}</a></li>
          <li>{t('header.menu6.title')}: <a href={`https://www.myclouddashboard.healthdash.lv?monitor=${user.name || user.email}`} target="_blank" rel="noreferrer">{user.name || 'Monitor Link'}</a></li>
        </ul>
      </div>

      <div className="health-charts-visualization mb-5 text-center">
        <h5>Personalized Health Insights</h5>
        <p className="text-muted small">Visualizing synchronized telemetry for {user.email}</p>
        <div className="chart-grid d-flex flex-wrap justify-content-center gap-2">
          {['daily', 'weekly', 'monthly', 'yearly'].map(period => (
            <React.Fragment key={period}>
              <RetryImage src={`https://healthdash-demo-1.s3.amazonaws.com/${sanitizedEmail}/${period}_sleep.png`} className="img-fluid border rounded shadow-sm" style={{ maxWidth: '200px' }} alt={`${period} Sleep`} />
              <RetryImage src={`https://healthdash-demo-1.s3.amazonaws.com/${sanitizedEmail}/${period}_activity.png`} className="img-fluid border rounded shadow-sm" style={{ maxWidth: '200px' }} alt={`${period} Activity`} />
              <RetryImage src={`https://healthdash-demo-1.s3.amazonaws.com/${sanitizedEmail}/${period}_readiness.png`} className="img-fluid border rounded shadow-sm" style={{ maxWidth: '200px' }} alt={`${period} Readiness`} />
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="cards-list mb-4">
        <h3>Your Health Cards</h3>
        {healthCards.length > 0 ? healthCards.map(card => (
          <div className="health-card border rounded p-3 mb-2 shadow-sm" key={card.id}>
            <div className="card-header d-flex justify-content-between align-items-center">
              <strong>{card.title}</strong>
              <span className="badge bg-info text-dark">
                Source: {card.meta?.dataSource || 'Android Sync Target'}
              </span>
            </div>
            <div className="card-content mt-2">
              <p className="mb-1">{card.summary}</p>
              <small className="text-muted">Processed via DSV</small>
            </div>
          </div>
        )) : (
          <p className="text-muted italic">No health cards synchronized yet. Please sync from your HealthDash Android app.</p>
        )}
      </div>
      
      <div className="adsense-container mt-4">
        <AdSense.Google
          client={process.env.REACT_APP_GADS_ID || 'ca-pub-2835578352930332'}
          slot='7806394673'
          style={{ display: 'block' }}
          format='auto'
          responsive='true'
          layoutKey='-gw-1+2a-9x+5c'
        />
      </div>

      {/* AWS Chatbot support for all tiers */}
      <AwsChatbot />
    </div>
  );
};

export default ProfilePage;